import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, Sparkles, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';

/**
 * Floating Chat Assistant — "Neighbour" powered by Claude.
 * Always available bottom-right. Helps anyone stuck after smart-search.
 */
export default function ChatWidget() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionIdRef = useRef(`tn-${Math.random().toString(36).slice(2, 12)}`);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        text: "G'day 👋 I'm Neighbour — the friendly assistant. Describe any home problem in your own words, or ask me anything. I'll point you to the right tradie.",
        suggested: null,
      }]);
    }
  }, [open, messages.length]);

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setMessages((m) => [...m, { role: 'user', text: q }]);
    setInput('');
    setBusy(true);
    try {
      const r = await api.aiChat({ session_id: sessionIdRef.current, message: q });
      setMessages((m) => [...m, { role: 'assistant', text: r.data.reply, suggested: r.data.suggested_category }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', text: "Sorry — I had a hiccup. Try again in a moment.", suggested: null }]);
    } finally {
      setBusy(false);
    }
  };

  const goSearch = (categoryId) => {
    setOpen(false);
    navigate(`/search?q=${categoryId}`);
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center bg-[#1A362D] text-white shadow-xl transition-all hover:scale-105 hover:bg-[#122820]"
          data-testid="chat-widget-toggle"
          aria-label="Open chat assistant"
        >
          <MessageCircle size={22} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-40 flex h-[560px] w-[380px] max-w-[calc(100vw-2rem)] flex-col border border-[#1C1B1A] bg-white shadow-2xl" data-testid="chat-widget-panel">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[#E5E4E0] bg-[#1A362D] px-4 py-3 text-white">
            <div className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center bg-[#D96C4E]">
                <Sparkles size={14} />
              </div>
              <div>
                <div className="font-heading text-sm font-bold">Neighbour</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">Your friendly guide</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/80 hover:text-white" data-testid="chat-widget-close">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-[#FAF9F6] p-4">
            {messages.map((m, i) => (
              <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'} data-testid={`chat-msg-${i}`}>
                <div className={`max-w-[85%] whitespace-pre-wrap border px-3 py-2 text-sm leading-relaxed ${
                  m.role === 'user'
                    ? 'border-[#1A362D] bg-[#1A362D] text-white'
                    : 'border-[#E5E4E0] bg-white text-[#1C1B1A]'
                }`}>
                  {m.text}
                  {m.suggested && (
                    <button
                      onClick={() => goSearch(m.suggested.id)}
                      className="mt-3 flex w-full items-center justify-between gap-2 border border-[#1A362D] bg-[#1A362D] px-3 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-white hover:bg-[#122820]"
                      data-testid={`chat-suggested-${m.suggested.id}`}
                    >
                      Search {m.suggested.display}s <ArrowRight size={12} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="chat-typing">
                <div className="border border-[#E5E4E0] bg-white px-3 py-2 text-sm text-[#8A8988]">
                  <span className="inline-block animate-pulse">Neighbour is thinking…</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2 border-t border-[#E5E4E0] bg-white p-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about any home problem…"
              className="input-base flex-1"
              data-testid="chat-widget-input"
              disabled={busy}
            />
            <button type="submit" disabled={busy || !input.trim()} className="btn-primary !py-2" data-testid="chat-widget-send">
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
